import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getApiBase,
  getAuthToken,
  isBlogEditor,
  getStoredUser,
} from "../utils/authClient";
import { fetchBlogPostBySlug } from "../api/blogApi";
import "./AdminBlog.css";

function newId() {
  return `b-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function slugFromTitle(title) {
  const s = String(title || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return (s || "articulo").slice(0, 96);
}

/** Convierte `content` del API/Mongo al estado del editor por bloques. */
function contentToBlocks(content) {
  if (!Array.isArray(content) || content.length === 0) {
    return [emptyBlock("p")];
  }
  return content.map((c) => {
    if (!c || typeof c !== "object") return emptyBlock("p");
    const id = newId();
    const type = c.type || "p";
    if (type === "h3") {
      return { id, type: "h3", text: String(c.text || "") };
    }
    if (type === "ul") {
      return {
        id,
        type: "ul",
        itemsText: Array.isArray(c.items) ? c.items.join("\n") : "",
      };
    }
    if (type === "note") {
      return { id, type: "note", text: String(c.text || "") };
    }
    if (type === "img") {
      return {
        id,
        type: "img",
        src: String(c.src || ""),
        alt: String(c.alt || ""),
      };
    }
    return { id, type: "p", text: String(c.text || "") };
  });
}

function emptyBlock(type) {
  const id = newId();
  switch (type) {
    case "h3":
      return { id, type: "h3", text: "" };
    case "ul":
      return { id, type: "ul", itemsText: "" };
    case "note":
      return { id, type: "note", text: "" };
    case "img":
      return { id, type: "img", src: "", alt: "" };
    default:
      return { id, type: "p", text: "" };
  }
}

function blocksToApiContent(blocks) {
  return blocks
    .map((b) => {
      if (b.type === "h3") {
        const text = String(b.text || "").trim();
        return text ? { type: "h3", text } : null;
      }
      if (b.type === "ul") {
        const items = String(b.itemsText || "")
          .split(/\r?\n/)
          .map((l) => l.trim())
          .filter(Boolean);
        return items.length ? { type: "ul", items } : null;
      }
      if (b.type === "note") {
        const text = String(b.text || "").trim();
        return text ? { type: "note", text } : null;
      }
      if (b.type === "img") {
        const src = String(b.src || "").trim();
        if (!src) return null;
        return {
          type: "img",
          src,
          alt: String(b.alt || "").trim(),
        };
      }
      const text = String(b.text || "").trim();
      return text ? { type: "p", text } : null;
    })
    .filter(Boolean);
}

function BlockRow({
  block,
  index,
  total,
  onChange,
  onRemove,
  onMove,
  onUploadImage,
  uploading,
}) {
  const label =
    block.type === "p"
      ? "Párrafo"
      : block.type === "h3"
        ? "Subtítulo"
        : block.type === "ul"
          ? "Lista (una línea = un ítem)"
          : block.type === "note"
            ? "Nota destacada"
            : "Imagen en el artículo";

  return (
    <div className="admin_blog_block">
      <div className="admin_blog_block_toolbar">
        <span className="admin_blog_block_label">{label}</span>
        <div className="admin_blog_block_btns">
          <button
            type="button"
            className="admin_blog_icon_btn"
            disabled={index <= 0}
            onClick={() => onMove(index, -1)}
            aria-label="Subir bloque"
          >
            ↑
          </button>
          <button
            type="button"
            className="admin_blog_icon_btn"
            disabled={index >= total - 1}
            onClick={() => onMove(index, 1)}
            aria-label="Bajar bloque"
          >
            ↓
          </button>
          <button
            type="button"
            className="admin_blog_icon_btn admin_blog_icon_danger"
            onClick={() => onRemove(block.id)}
            aria-label="Eliminar bloque"
          >
            ✕
          </button>
        </div>
      </div>

      {block.type === "img" ? (
        <div className="admin_blog_block_img">
          <label>
            Texto alternativo (accesibilidad)
            <input
              value={block.alt}
              onChange={(e) => onChange(block.id, { alt: e.target.value })}
              placeholder="Describe la imagen"
            />
          </label>
          <label className="admin_blog_file">
            Archivo imagen
            <input
              type="file"
              accept="image/*"
              disabled={uploading}
              onChange={(e) => onUploadImage(block.id, e.target.files && e.target.files[0])}
            />
            {uploading ? <span className="admin_blog_hint">Subiendo…</span> : null}
          </label>
          {block.src ? (
            <div className="admin_blog_img_preview_wrap">
              <img src={block.src} alt="" className="admin_blog_img_preview" />
              <span className="admin_blog_path_ok">{block.src}</span>
            </div>
          ) : (
            <span className="admin_blog_hint">Sube una imagen para este bloque.</span>
          )}
        </div>
      ) : block.type === "ul" ? (
        <textarea
          rows={5}
          value={block.itemsText}
          onChange={(e) => onChange(block.id, { itemsText: e.target.value })}
          placeholder={"Cada línea es un ítem de la lista.\nEj.: Cepillar 2 veces al día\nUsar hilo dental"}
        />
      ) : block.type === "h3" ? (
        <input
          type="text"
          value={block.text}
          onChange={(e) => onChange(block.id, { text: e.target.value })}
          placeholder="Título de sección"
        />
      ) : (
        <textarea
          rows={block.type === "note" ? 4 : 6}
          value={block.text}
          onChange={(e) => onChange(block.id, { text: e.target.value })}
          placeholder={
            block.type === "note"
              ? "Aviso destacado para el lector…"
              : "Texto del párrafo…"
          }
        />
      )}
    </div>
  );
}

export default function AdminBlog() {
  const navigate = useNavigate();
  const { slug: editRouteSlug } = useParams();
  const isEditMode = Boolean(editRouteSlug);
  const [editLoading, setEditLoading] = useState(isEditMode);
  const [userLabel, setUserLabel] = useState("");
  const [coverPath, setCoverPath] = useState("");
  const [coverUploading, setCoverUploading] = useState(false);
  const [blockUploadId, setBlockUploadId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    slug: "",
    locale: "es",
    title: "",
    date: new Date().toISOString().slice(0, 10),
    readTime: "4 min",
    excerpt: "",
    seoTitle: "",
    seoDescription: "",
    coverTheme: "mint",
    serviceFocus: "orthodontics",
  });
  const [blocks, setBlocks] = useState(() => [emptyBlock("p")]);

  useEffect(() => {
    const u = getStoredUser();
    if (!getAuthToken() || !isBlogEditor()) {
      toast.error("Inicia sesión con una cuenta admin o editor.");
      navigate("/login_user");
      return;
    }
    setUserLabel(u.email || "");
  }, [navigate]);

  useEffect(() => {
    if (!isEditMode || !editRouteSlug) {
      setEditLoading(false);
      return undefined;
    }
    if (!getAuthToken() || !isBlogEditor()) return undefined;
    let cancelled = false;
    (async () => {
      setEditLoading(true);
      try {
        const post = await fetchBlogPostBySlug(editRouteSlug);
        if (cancelled) return;
        if (!post || !post.slug) {
          toast.error("No se encontró el artículo.");
          navigate("/blog");
          return;
        }
        const sf = Array.isArray(post.serviceFocus)
          ? post.serviceFocus.join(",")
          : String(post.serviceFocus || "");
        setForm({
          slug: post.slug,
          locale: post.locale || "es",
          title: post.title || "",
          date: post.date || new Date().toISOString().slice(0, 10),
          readTime: post.readTime || "4 min",
          excerpt: post.excerpt || "",
          seoTitle: post.seoTitle || post.title || "",
          seoDescription: post.seoDescription || post.excerpt || "",
          coverTheme: post.coverTheme || "mint",
          serviceFocus: sf || "orthodontics",
        });
        setCoverPath(post.coverImage || post.coverImagePath || "");
        setBlocks(contentToBlocks(post.content));
      } catch (e) {
        if (!cancelled) {
          toast.error(e.message || "No se pudo cargar el artículo.");
          navigate("/blog");
        }
      } finally {
        if (!cancelled) setEditLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEditMode, editRouteSlug, navigate]);

  const apiBase = getApiBase();
  const token = getAuthToken();

  const uploadImageFile = useCallback(
    async (file) => {
      const fd = new FormData();
      fd.append("image", file);
      const res = await fetch(`${apiBase}/api/blog/upload-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Error al subir imagen");
      }
      return data.coverImagePath;
    },
    [apiBase, token]
  );

  const handleCoverFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const path = await uploadImageFile(file);
      setCoverPath(path);
      toast.success("Portada subida al servidor.");
    } catch (err) {
      toast.error(err.message || "Error al subir");
    } finally {
      setCoverUploading(false);
      e.target.value = "";
    }
  };

  const handleBlockImage = async (blockId, file) => {
    if (!file) return;
    setBlockUploadId(blockId);
    try {
      const path = await uploadImageFile(file);
      setBlocks((prev) =>
        prev.map((b) => (b.id === blockId ? { ...b, src: path } : b))
      );
      toast.success("Imagen insertada en el artículo.");
    } catch (err) {
      toast.error(err.message || "Error al subir");
    } finally {
      setBlockUploadId(null);
    }
  };

  const updateBlock = (id, patch) => {
    setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  };

  const removeBlock = (id) => {
    setBlocks((prev) => (prev.length <= 1 ? prev : prev.filter((b) => b.id !== id)));
  };

  const moveBlock = (index, delta) => {
    setBlocks((prev) => {
      const j = index + delta;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      next.splice(j, 0, removed);
      return next;
    });
  };

  const addBlock = (type) => {
    setBlocks((prev) => [...prev, emptyBlock(type)]);
  };

  const applySlugFromTitle = () => {
    setForm((f) => ({ ...f, slug: slugFromTitle(f.title) }));
    toast.info("Slug generado desde el título (revísalo antes de publicar).");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const content = blocksToApiContent(blocks);
    const hasBody = content.some((c) => {
      if (c.type === "img") return true;
      if (c.type === "ul") return Array.isArray(c.items) && c.items.length > 0;
      return Boolean(c.text && String(c.text).trim());
    });

    if (!hasBody) {
      toast.error("Añade al menos un bloque con texto o una imagen en el cuerpo.");
      return;
    }

    const serviceFocus = form.serviceFocus
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    const body = {
      slug: form.slug.trim(),
      locale: form.locale,
      title: form.title.trim(),
      date: form.date,
      readTime: form.readTime.trim(),
      excerpt: form.excerpt.trim(),
      seoTitle: form.seoTitle.trim() || form.title.trim(),
      seoDescription: form.seoDescription.trim() || form.excerpt.trim(),
      coverTheme: form.coverTheme,
      coverImagePath: coverPath,
      serviceFocus,
      content,
    };

    if (!body.slug || !body.title) {
      toast.error("Slug y título son obligatorios.");
      return;
    }
    if (!coverPath) {
      toast.error("Sube una imagen de portada (aparece en el listado del blog).");
      return;
    }

    setSaving(true);
    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };
      let res;
      let data;

      if (isEditMode && editRouteSlug) {
        if (body.slug !== editRouteSlug) {
          throw new Error("El slug del formulario no coincide con el artículo en edición.");
        }
        res = await fetch(
          `${apiBase}/api/blog/posts/${encodeURIComponent(editRouteSlug)}`,
          {
            method: "PUT",
            headers,
            body: JSON.stringify(body),
          }
        );
        data = await res.json().catch(() => ({}));
      } else {
        res = await fetch(`${apiBase}/api/blog/posts`, {
          method: "POST",
          headers,
          body: JSON.stringify(body),
        });
        data = await res.json().catch(() => ({}));

        if (res.status === 409) {
          res = await fetch(
            `${apiBase}/api/blog/posts/${encodeURIComponent(body.slug)}`,
            {
              method: "PUT",
              headers,
              body: JSON.stringify(body),
            }
          );
          data = await res.json().catch(() => ({}));
        }
      }

      if (!res.ok) {
        throw new Error(data.message || "No se pudo guardar el post");
      }
      toast.success(
        isEditMode
          ? "Artículo actualizado correctamente."
          : res.status === 201
            ? "Artículo publicado. Ya aparece en el blog."
            : "Artículo actualizado correctamente."
      );
      navigate(`/blog/${body.slug}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (editLoading) {
    return (
      <div className="admin_blog_page">
        <div className="admin_blog_layout">
          <p className="admin_blog_hint" style={{ padding: "24px 0" }}>
            Cargando artículo…
          </p>
        </div>
        <ToastContainer theme="dark" />
      </div>
    );
  }

  return (
    <div className="admin_blog_page">
      <div className="admin_blog_layout">
        <header className="admin_blog_header">
          <p className="admin_blog_kicker">Administrador de contenido</p>
          <h1>{isEditMode ? "Editar artículo del blog" : "Nuevo artículo del blog"}</h1>
          <p className="admin_blog_meta">
            Sesión: <strong>{userLabel}</strong> ·{" "}
            {isEditMode
              ? "Los cambios se guardan al pulsar «Guardar cambios»."
              : "Los cambios se guardan en el servidor al publicar."}
          </p>
        </header>

        <form className="admin_blog_form_stack" onSubmit={handleSubmit}>
          <section className="admin_blog_card">
            <h2 className="admin_blog_card_title">Información principal</h2>
            <div className="admin_blog_grid2">
              <label className="admin_blog_field">
                Título del post
                <input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Ej. Brackets vs alineadores en Cusco: guía breve"
                  required
                />
              </label>
              <label className="admin_blog_field">
                Slug (URL)
                <div className="admin_blog_slug_row">
                  <input
                    name="slug"
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                    placeholder="ortodoncia-cusco-guia"
                    required
                    disabled={isEditMode}
                    title={isEditMode ? "El slug no se puede cambiar al editar un artículo existente." : undefined}
                  />
                  <button
                    type="button"
                    className="admin_blog_secondary"
                    onClick={applySlugFromTitle}
                    disabled={isEditMode}
                  >
                    Generar desde título
                  </button>
                </div>
              </label>
            </div>
            <div className="admin_blog_grid3">
              <label className="admin_blog_field">
                Idioma
                <select
                  value={form.locale}
                  onChange={(e) => setForm({ ...form, locale: e.target.value })}
                >
                  <option value="es">Español</option>
                  <option value="en">English</option>
                </select>
              </label>
              <label className="admin_blog_field">
                Fecha de publicación
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                />
              </label>
              <label className="admin_blog_field">
                Tiempo de lectura
                <input
                  value={form.readTime}
                  onChange={(e) => setForm({ ...form, readTime: e.target.value })}
                  placeholder="4 min"
                />
              </label>
            </div>
            <label className="admin_blog_field">
              Resumen (extracto en tarjetas del blog)
              <textarea
                rows={3}
                value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                placeholder="2–3 líneas que inviten a leer el artículo completo."
              />
            </label>
          </section>

          <section className="admin_blog_card">
            <h2 className="admin_blog_card_title">Portada e imagen principal</h2>
            <p className="admin_blog_card_help">
              Esta imagen se muestra en la lista del blog y arriba del artículo. Se sube al
              servidor en <code>/uploads/blog/</code>.
            </p>
            <div className="admin_blog_grid2">
              <label className="admin_blog_field admin_blog_file">
                Subir imagen de portada
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleCoverFile}
                  disabled={coverUploading}
                />
                {coverUploading ? <span className="admin_blog_hint">Subiendo…</span> : null}
              </label>
              <label className="admin_blog_field">
                Estilo de tarjeta
                <select
                  value={form.coverTheme}
                  onChange={(e) => setForm({ ...form, coverTheme: e.target.value })}
                >
                  <option value="mint">Mint</option>
                  <option value="sky">Sky</option>
                  <option value="peach">Peach</option>
                </select>
              </label>
            </div>
            {coverPath ? (
              <div className="admin_blog_cover_preview">
                <img src={coverPath} alt="Vista previa portada" />
                <span className="admin_blog_path_ok">{coverPath}</span>
              </div>
            ) : (
              <p className="admin_blog_hint">Obligatorio publicar: sube la portada primero.</p>
            )}
          </section>

          <section className="admin_blog_card">
            <h2 className="admin_blog_card_title">SEO y etiquetas</h2>
            <label className="admin_blog_field">
              Título SEO (opcional)
              <input
                value={form.seoTitle}
                onChange={(e) => setForm({ ...form, seoTitle: e.target.value })}
                placeholder="Si lo dejas vacío, se usa el título del post"
              />
            </label>
            <label className="admin_blog_field">
              Descripción SEO
              <textarea
                rows={2}
                value={form.seoDescription}
                onChange={(e) => setForm({ ...form, seoDescription: e.target.value })}
                placeholder="Para buscadores y redes sociales"
              />
            </label>
            <label className="admin_blog_field">
              Enfoque de servicios (separados por coma)
              <input
                value={form.serviceFocus}
                onChange={(e) => setForm({ ...form, serviceFocus: e.target.value })}
                placeholder="orthodontics"
              />
            </label>
          </section>

          <section className="admin_blog_card admin_blog_card_wide">
            <div className="admin_blog_card_head">
              <h2 className="admin_blog_card_title">Cuerpo del artículo</h2>
              <div className="admin_blog_add_row">
                <span className="admin_blog_add_label">Añadir bloque:</span>
                <button type="button" className="admin_blog_chip" onClick={() => addBlock("p")}>
                  + Párrafo
                </button>
                <button type="button" className="admin_blog_chip" onClick={() => addBlock("h3")}>
                  + Subtítulo
                </button>
                <button type="button" className="admin_blog_chip" onClick={() => addBlock("ul")}>
                  + Lista
                </button>
                <button type="button" className="admin_blog_chip" onClick={() => addBlock("note")}>
                  + Nota
                </button>
                <button type="button" className="admin_blog_chip" onClick={() => addBlock("img")}>
                  + Imagen
                </button>
              </div>
            </div>
            <p className="admin_blog_card_help">
              Ordena los bloques con las flechas. Las imágenes del cuerpo también se guardan en el
              servidor.
            </p>
            <div className="admin_blog_blocks">
              {blocks.map((block, index) => (
                <BlockRow
                  key={block.id}
                  block={block}
                  index={index}
                  total={blocks.length}
                  onChange={updateBlock}
                  onRemove={removeBlock}
                  onMove={moveBlock}
                  onUploadImage={handleBlockImage}
                  uploading={blockUploadId === block.id}
                />
              ))}
            </div>
          </section>

          <div className="admin_blog_actions">
            <button type="submit" disabled={saving || !coverPath}>
              {saving
                ? isEditMode
                  ? "Guardando…"
                  : "Publicando…"
                : isEditMode
                  ? "Guardar cambios"
                  : "Publicar en el blog"}
            </button>
            <Link to="/blog" className="admin_blog_link">
              Ver blog público
            </Link>
            <Link to="/admin-person" className="admin_blog_link">
              Volver al panel
            </Link>
          </div>
        </form>
      </div>
      <ToastContainer theme="dark" />
    </div>
  );
}
